import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, storage } from "../firebase";
import {
  collection,
  addDoc,
  query,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Avatar,
  AppBar,
  Toolbar,
  CircularProgress,
  Zoom,
  ClickAwayListener,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import EmojiEmotionsOutlinedIcon from "@mui/icons-material/EmojiEmotionsOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CallIcon from "@mui/icons-material/Call";
import VideocamIcon from "@mui/icons-material/Videocam";
import EmojiPicker from "emoji-picker-react";
import { isLoggedIn } from "../session";
import { useCall } from "../CallProvider";

// ── helpers ────────────────────────────────────────────────────────────────
const formatTime = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
};

const formatDateLabel = (ts) => {
  if (!ts) return "";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const now = new Date();
  const isToday =
    d.getDate() === now.getDate() &&
    d.getMonth() === now.getMonth() &&
    d.getFullYear() === now.getFullYear();
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  const isYesterday =
    d.getDate() === yesterday.getDate() &&
    d.getMonth() === yesterday.getMonth() &&
    d.getFullYear() === yesterday.getFullYear();
  if (isToday) return "Hoy";
  if (isYesterday) return "Ayer";
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long" });
};

const sameDay = (ts1, ts2) => {
  if (!ts1 || !ts2) return false;
  const d1 = ts1.toDate ? ts1.toDate() : new Date(ts1);
  const d2 = ts2.toDate ? ts2.toDate() : new Date(ts2);
  return (
    d1.getDate() === d2.getDate() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getFullYear() === d2.getFullYear()
  );
};

const formatLastSeen = (ts) => {
  if (!ts) return null;
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  const diff = Date.now() - d.getTime();
  if (diff < 60000) return "en línea";
  if (diff < 3600000) return `visto hace ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000)
    return `visto a las ${d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
  return `visto el ${d.toLocaleDateString("es-ES", { day: "numeric", month: "short" })}`;
};


// ──────────────────────────────────────────────────────────────────────────

// ── WebRTC config ──────────────────────────────────────────────────────────

// ──────────────────────────────────────────────────────────────────────────

function DirectChatBox() {
  const { chatId, otherId, authId } = useParams();
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [authUserId, setAuthUserId] = useState(authId || null);
  const [authUserName, setAuthUserName] = useState("");
  const [authUserImage, setAuthUserImage] = useState("");
  const [otherUser, setOtherUser] = useState({ displayName: "Usuario", photoURL: "" });
  const [loading, setLoading] = useState(true);
  const [showScrollBtn, setShowScrollBtn] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const messagesEndRef = useRef(null);
  const scrollBoxRef = useRef(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);
  const navigate = useNavigate();
  const { startCall } = useCall();

  // ── init ──────────────────────────────────────────────────────────────
  useEffect(() => {
    const init = async () => {
      const user = await isLoggedIn();
      if (user) {
        setAuthUserId(user.uid);
        const userSnap = await getDoc(doc(db, "users", user.uid));
        if (userSnap.exists()) {
          setAuthUserName(userSnap.data().displayName || "");
          setAuthUserImage(userSnap.data().profileImageUrl || "");
        }
      }
      if (otherId) {
        const otherSnap = await getDoc(doc(db, "users", otherId));
        if (otherSnap.exists()) setOtherUser(otherSnap.data());
      }
    };
    init();
  }, [otherId]);

  // ── messages listener ─────────────────────────────────────────────────
  useEffect(() => {
    if (!chatId) return;
    const messagesRef = collection(db, `directChats/${chatId}/messages`);
    const q = query(messagesRef, orderBy("timestamp", "asc"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      setMessages(msgs);
      setLoading(false);
      // Only auto-scroll if user is near the bottom
      const box = scrollBoxRef.current;
      const nearBottom = !box || box.scrollHeight - box.scrollTop - box.clientHeight < 120;
      if (nearBottom) {
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
      }
    });
    return () => unsubscribe();
  }, [chatId]);

  // ── mark as read ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!authUserId || !chatId || messages.length === 0) return;

    // Mark unread incoming messages as read (updates the sender's tick to blue/double)
    const unread = messages.filter((m) => m.senderId !== authUserId && !m.read);
    if (unread.length > 0) {
      const batch = writeBatch(db);
      unread.forEach((m) => {
        batch.update(doc(db, `directChats/${chatId}/messages`, m.id), { read: true });
      });
      batch.commit().catch(() => {});
    }

    // Reset unread counter for current user
    updateDoc(doc(db, "directChats", chatId), {
      [`unreadCount.${authUserId}`]: 0,
    }).catch(() => {});
  }, [chatId, authUserId, messages]);

  // ── scroll detection ─────────────────────────────────────────────────
  const handleScroll = useCallback(() => {
    const box = scrollBoxRef.current;
    if (!box) return;
    const distFromBottom = box.scrollHeight - box.scrollTop - box.clientHeight;
    setShowScrollBtn(distFromBottom > 180);
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  // ── send ──────────────────────────────────────────────────────────────
  const handleSend = async () => {
    if (!newMessage.trim() || !authUserId) return;
    const text = newMessage.trim();
    setNewMessage("");

    const chatDocRef = doc(db, "directChats", chatId);
    const messagesRef = collection(db, `directChats/${chatId}/messages`);

    try {
      await addDoc(messagesRef, {
        content: text,
        senderId: authUserId,
        senderName: authUserName,
        receiverId: otherId,
        timestamp: serverTimestamp(),
        read: false,
      });
      await setDoc(
        chatDocRef,
        {
          participants: [authUserId, otherId].sort(),
          lastMessage: text,
          lastMessageAt: serverTimestamp(),
          [`unreadCount.${otherId}`]: (messages.filter((m) => m.senderId !== otherId).length + 1),
          [`unreadCount.${authUserId}`]: 0,
        },
        { merge: true }
      );
      await addDoc(collection(db, "notifications"), {
        userId: otherId,
        fromUid: authUserId,
        fromName: authUserName,
        fromImage: authUserImage,
        type: "message",
        text: "te envió un mensaje",
        read: false,
        createdAt: serverTimestamp(),
        relatedId: chatId,
      });
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 80);
    } catch (err) {
      console.error("Error enviando mensaje:", err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEmojiClick = (emojiData) => {
    setNewMessage((prev) => prev + emojiData.emoji);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !authUserId) return;
    e.target.value = "";
    setUploadingImage(true);
    try {
      const storageRef = ref(storage, `chatImages/${chatId}/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      const chatDocRef = doc(db, "directChats", chatId);
      const messagesRef = collection(db, `directChats/${chatId}/messages`);
      await addDoc(messagesRef, {
        content: "",
        imageUrl: url,
        senderId: authUserId,
        senderName: authUserName,
        receiverId: otherId,
        timestamp: serverTimestamp(),
        read: false,
      });
      await setDoc(chatDocRef, {
        participants: [authUserId, otherId].sort(),
        lastMessage: "📷 Imagen",
        lastMessageAt: serverTimestamp(),
        [`unreadCount.${otherId}`]: (messages.filter((m) => m.senderId !== otherId).length + 1),
        [`unreadCount.${authUserId}`]: 0,
      }, { merge: true });
      await addDoc(collection(db, "notifications"), {
        userId: otherId,
        fromUid: authUserId,
        fromName: authUserName,
        fromImage: authUserImage,
        type: "message",
        text: "te envió una imagen",
        read: false,
        createdAt: serverTimestamp(),
        relatedId: chatId,
      });
    } catch (err) {
      console.error("Error subiendo imagen:", err);
    } finally {
      setUploadingImage(false);
    }
  };

  // ── status label ──────────────────────────────────────────────────────
  const statusLabel = formatLastSeen(otherUser.lastSeen);
  const isOnline = statusLabel === "en línea";

  // ── render ────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh", bgcolor: "#eef2fb" }}>

      {/* ── Header ── */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "white",
          color: "text.primary",
          borderBottom: "1px solid #e8eaf0",
        }}
      >
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton edge="start" onClick={() => navigate(-1)} sx={{ mr: 0.5 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box
            sx={{ position: "relative", mr: 1.5, cursor: "pointer" }}
            onClick={() => navigate(`/perfil/${otherId}`)}
          >
            <Avatar
              src={otherUser.photoURL || ""}
              alt={otherUser.displayName}
              sx={{ width: 42, height: 42, border: "2px solid #1976d2" }}
            >
              {(otherUser.displayName || "?")[0].toUpperCase()}
            </Avatar>
            {isOnline && (
              <Box
                sx={{
                  position: "absolute",
                  bottom: 1,
                  right: 1,
                  width: 11,
                  height: 11,
                  bgcolor: "#43a047",
                  borderRadius: "50%",
                  border: "2px solid white",
                }}
              />
            )}
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              lineHeight={1.2}
              noWrap
              sx={{ cursor: "pointer" }}
              onClick={() => navigate(`/perfil/${otherId}`)}
            >
              {otherUser.displayName || "Usuario"}
            </Typography>
            {statusLabel && (
              <Typography
                variant="caption"
                sx={{ color: isOnline ? "#43a047" : "text.secondary" }}
              >
                {statusLabel}
              </Typography>
            )}
          </Box>
          <IconButton title="Llamada de voz" onClick={() => startCall("audio", otherId, { displayName: otherUser.displayName || "Usuario", photoURL: otherUser.photoURL || "" })} sx={{ color: "text.secondary" }}>
            <CallIcon />
          </IconButton>
          <IconButton title="Videollamada" onClick={() => startCall("video", otherId, { displayName: otherUser.displayName || "Usuario", photoURL: otherUser.photoURL || "" })} sx={{ color: "text.secondary" }}>
            <VideocamIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* ── Messages area ── */}
      <Box
        ref={scrollBoxRef}
        onScroll={handleScroll}
        sx={{
          flex: 1,
          overflowY: "auto",
          pt: "72px",
          pb: "70px",
          px: 1.5,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 10 }}>
            <CircularProgress size={32} />
          </Box>
        ) : messages.length === 0 ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                mt: 10,
                gap: 1.5,
                color: "text.secondary",
              }}
            >
              <Avatar
                src={otherUser.photoURL || ""}
                sx={{ width: 72, height: 72, border: "3px solid #1976d2" }}
              >
                {(otherUser.displayName || "?")[0].toUpperCase()}
              </Avatar>
              <Typography variant="subtitle1" fontWeight="600">
                {otherUser.displayName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Iniciá la conversación 👋
              </Typography>
            </Box>
        ) : (
          <>
          <Box sx={{ flex: 1 }} />
          {messages.map((msg, i) => {
            const isMine = msg.senderId === authUserId;
            const prev = messages[i - 1];
            const next = messages[i + 1];

            // Grouping: consecutive messages from same sender within 2 min
            const prevSame =
              prev &&
              prev.senderId === msg.senderId &&
              msg.timestamp &&
              prev.timestamp &&
              Math.abs(
                (msg.timestamp.toDate?.() ?? new Date(msg.timestamp)) -
                  (prev.timestamp.toDate?.() ?? new Date(prev.timestamp))
              ) < 120000;
            const nextSame =
              next &&
              next.senderId === msg.senderId &&
              msg.timestamp &&
              next.timestamp &&
              Math.abs(
                (next.timestamp.toDate?.() ?? new Date(next.timestamp)) -
                  (msg.timestamp.toDate?.() ?? new Date(msg.timestamp))
              ) < 120000;

            const showAvatar = !isMine && !nextSame;
            const showDateSep = !prev || !sameDay(prev.timestamp, msg.timestamp);

            // Border radius per group position
            const r = 18;
            const rSmall = 5;
            const borderRadius = isMine
              ? `${prevSame ? rSmall : r}px ${r}px ${r}px ${nextSame ? rSmall : r}px`
              : `${r}px ${prevSame ? rSmall : r}px ${nextSame ? rSmall : r}px ${r}px`;

            return (
              <React.Fragment key={msg.id}>
                {/* Date separator */}
                {showDateSep && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      my: 2,
                      gap: 1,
                    }}
                  >
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "#dde1ec" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        px: 1.5,
                        py: 0.4,
                        bgcolor: "#dde1ec",
                        borderRadius: "12px",
                        color: "#666",
                        fontWeight: 500,
                        whiteSpace: "nowrap",
                      }}
                    >
                      {formatDateLabel(msg.timestamp)}
                    </Typography>
                    <Box sx={{ flex: 1, height: "1px", bgcolor: "#dde1ec" }} />
                  </Box>
                )}

                {/* Message row */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: isMine ? "flex-end" : "flex-start",
                    alignItems: "flex-end",
                    gap: 0.5,
                    mb: nextSame ? 0.3 : 1,
                  }}
                >
                  {/* Other user avatar placeholder to keep alignment */}
                  {!isMine && (
                    <Box sx={{ width: 30, flexShrink: 0 }}>
                      {showAvatar && (
                        <Avatar
                          src={otherUser.photoURL || ""}
                          sx={{ width: 28, height: 28 }}
                        >
                          {(otherUser.displayName || "?")[0].toUpperCase()}
                        </Avatar>
                      )}
                    </Box>
                  )}

                  <Box
                    sx={{
                      maxWidth: "72%",
                      bgcolor: msg.imageUrl ? "transparent" : (isMine ? "#1976d2" : "white"),
                      color: isMine ? "white" : "text.primary",
                      borderRadius: msg.imageUrl ? "14px" : borderRadius,
                      px: msg.imageUrl ? 0 : 1.8,
                      py: msg.imageUrl ? 0 : 0.8,
                      boxShadow: msg.imageUrl ? "none" : "0 1px 3px rgba(0,0,0,0.10)",
                      overflow: "hidden",
                    }}
                  >
                    {msg.imageUrl ? (
                      <Box sx={{ position: "relative" }}>
                        <Box
                          component="img"
                          src={msg.imageUrl}
                          alt="imagen"
                          onClick={() => setLightboxUrl(msg.imageUrl)}
                          sx={{
                            display: "block",
                            maxWidth: 240,
                            maxHeight: 300,
                            borderRadius: "14px",
                            cursor: "pointer",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.18)",
                          }}
                        />
                        <Box
                          sx={{
                            position: "absolute",
                            bottom: 6,
                            right: 8,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.3,
                            bgcolor: "rgba(0,0,0,0.4)",
                            borderRadius: "8px",
                            px: 0.7,
                            py: 0.2,
                          }}
                        >
                          <Typography variant="caption" sx={{ color: "white", fontSize: "0.63rem" }}>
                            {formatTime(msg.timestamp)}
                          </Typography>
                          {isMine && (
                            <>
                              <Typography variant="caption" sx={{ fontSize: "0.62rem", color: msg.read ? "white" : "rgba(255,255,255,0.65)" }}>
                                {msg.read ? "Visto" : "Enviado"}
                              </Typography>
                              {msg.read
                                ? <DoneAllIcon sx={{ fontSize: 13, color: "#90caf9" }} />
                                : <DoneIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }} />
                              }
                            </>
                          )}
                        </Box>
                      </Box>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ wordBreak: "break-word", lineHeight: 1.5 }}>
                          {msg.content}
                        </Typography>
                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 0.3, mt: 0.3 }}>
                          <Typography variant="caption" sx={{ opacity: 0.75, fontSize: "0.63rem" }}>
                            {formatTime(msg.timestamp)}
                          </Typography>
                          {isMine && (
                            <>
                              <Typography variant="caption" sx={{ fontSize: "0.62rem", color: msg.read ? "rgba(255,255,255,1)" : "rgba(255,255,255,0.65)" }}>
                                {msg.read ? "Visto" : "Enviado"}
                              </Typography>
                              {msg.read
                                ? <DoneAllIcon sx={{ fontSize: 13, color: "#90caf9" }} />
                                : <DoneIcon sx={{ fontSize: 13, color: "rgba(255,255,255,0.6)" }} />
                              }
                            </>
                          )}
                        </Box>
                      </>
                    )}
                  </Box>
                </Box>
              </React.Fragment>
            );
          })}
          </>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* ── Scroll-to-bottom button ── */}
      <Zoom in={showScrollBtn}>
        <IconButton
          onClick={scrollToBottom}
          size="small"
          sx={{
            position: "fixed",
            bottom: 80,
            right: 16,
            bgcolor: "white",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
            zIndex: 10,
            "&:hover": { bgcolor: "#f0f4ff" },
          }}
        >
          <KeyboardArrowDownIcon />
        </IconButton>
      </Zoom>

      {/* ── Image Lightbox ── */}
      {lightboxUrl && (
        <Box
          onClick={() => setLightboxUrl(null)}
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(0,0,0,0.92)",
            zIndex: 2000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <IconButton
            onClick={() => setLightboxUrl(null)}
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              color: "white",
              bgcolor: "rgba(255,255,255,0.15)",
              "&:hover": { bgcolor: "rgba(255,255,255,0.25)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={lightboxUrl}
            alt="imagen"
            onClick={(e) => e.stopPropagation()}
            sx={{
              maxWidth: "95vw",
              maxHeight: "90vh",
              borderRadius: "10px",
              boxShadow: "0 4px 32px rgba(0,0,0,0.6)",
              objectFit: "contain",
            }}
          />
        </Box>
      )}

      {/* ── Emoji Picker ── */}
      {showEmojiPicker && (
        <ClickAwayListener onClickAway={() => setShowEmojiPicker(false)}>
          <Box sx={{ position: "fixed", bottom: 70, left: 8, zIndex: 1500 }}>
            <EmojiPicker
              onEmojiClick={handleEmojiClick}
              skinTonesDisabled
              searchDisabled={false}
              height={380}
              width={320}
              previewConfig={{ showPreview: false }}
            />
          </Box>
        </ClickAwayListener>
      )}

      {/* ── Input area ── */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: "white",
          px: 1,
          py: 0.8,
          display: "flex",
          alignItems: "flex-end",
          gap: 0.5,
          boxShadow: "0 -1px 6px rgba(0,0,0,0.07)",
        }}
      >
        {/* Hidden file inputs */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />
        <input
          ref={cameraInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          style={{ display: "none" }}
          onChange={handleImageUpload}
        />

        <TextField
          fullWidth
          multiline
          maxRows={4}
          size="small"
          placeholder="Escribí un mensaje..."
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            startAdornment: (
              <IconButton
                size="small"
                onClick={() => setShowEmojiPicker((v) => !v)}
                sx={{ color: showEmojiPicker ? "primary.main" : "text.secondary", mr: 0.5 }}
              >
                <EmojiEmotionsOutlinedIcon fontSize="small" />
              </IconButton>
            ),
            endAdornment: (
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <IconButton
                  size="small"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingImage}
                  sx={{ color: "text.secondary" }}
                >
                  <CameraAltOutlinedIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingImage}
                  sx={{ color: "text.secondary" }}
                >
                  {uploadingImage ? <CircularProgress size={18} /> : <AttachFileIcon fontSize="small" />}
                </IconButton>
                <IconButton
                  size="small"
                  onClick={handleSend}
                  disabled={!newMessage.trim()}
                  sx={{
                    bgcolor: newMessage.trim() ? "primary.main" : "#e0e0e0",
                    color: "white",
                    ml: 0.5,
                    transition: "background 0.2s",
                    "&:hover": { bgcolor: newMessage.trim() ? "primary.dark" : "#e0e0e0" },
                    width: 32,
                    height: 32,
                  }}
                >
                  <SendIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "22px",
              bgcolor: "#f4f6fb",
              "& fieldset": { borderColor: "#dde1ec" },
              "&:hover fieldset": { borderColor: "#1976d2" },
              pl: 0.5,
              pr: 0.5,
            },
          }}
        />
      </Box>

    </Box>
  );
}

export default DirectChatBox;
